'use client';

import { CardElement } from '@stripe/react-stripe-js';
import { StripeCardElement } from '@stripe/stripe-js';
import { PaymentMethods } from 'api-graphql-types/enums';
import { Heading } from 'design-system/client';
import { useTranslations } from 'next-intl';
import styles from '../StripeFields.module.css';
import useResetFieldOnPaymentMethodChange from '../useResetFieldOnPaymentMethodChange';
import { useDonationFormContext, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import { useIsStripeLoaded } from '@/donationPages/_dependencies/StripeContext';
import classNames from 'classnames';
import StripeFieldErrorMessage from '../StripeFieldErrorMessage';
import { getCcElementProps, invalidStyle } from '../stripeInputStyles';
import useStripeEventHandler, { StripeEventHandlerScope } from '../useStripeEventHandler';
function StripeCCFields() {
  const t = useTranslations('nextjs.donate');
  const {
    getFieldState
  } = useDonationFormContext();
  const [paymentMethod, layout] = useDonationFormValues(['payment_method', '_layout']);
  const {
    handleChange,
    handleBlur,
    complete
  } = useStripeEventHandler(StripeEventHandlerScope.CC);
  const isStripeLoaded = useIsStripeLoaded();
  const onReady = useResetFieldOnPaymentMethodChange<StripeCardElement>(paymentMethod);
  const show = paymentMethod === PaymentMethods.StripeCc;
  if (show && !isStripeLoaded) return <div className={styles.stripeNotLoadedMessage}>
        <Heading level="text-md-important" className={styles.label}>
          {t('stripe_cc_fields.headline')}
        </Heading>
        <p>{t('errors.stripe_cc_unavailable')}</p>
      </div>;
  if (!isStripeLoaded) return null;
  const stripeCompleteState = getFieldState('_stripe_complete');
  const stripeCompleteIsTouched = !!stripeCompleteState.isTouched;
  const stripeCompleteHasError = !!stripeCompleteState.error;
  const error = stripeCompleteHasError && stripeCompleteIsTouched;
  const showIframeIncompleteError = layout === 'iframe' && (error || !complete && stripeCompleteIsTouched);

  // because of the new library's behaviour unmounting Stripe components will cause an exception,
  // unless it's removed together with its provider
  return <div style={{
    display: show ? 'block' : 'none'
  }} className={classNames(styles.stripeField, {
    [styles.emptyError!]: showIframeIncompleteError
  })}>
      <Heading level="text-md-important" className={styles.label}>
        {t('stripe_cc_fields.headline')}*
      </Heading>
      <CardElement onReady={onReady} onChange={handleChange} onBlur={handleBlur} id="card-element" {...getCcElementProps({
      empty: showIframeIncompleteError ? invalidStyle : {}
    })} />
      {/* No accessible error message for you */}
      <StripeFieldErrorMessage className={styles.stripeFieldError} incompleteMessage={t('errors.stripe_cc_incomplete_number')} />
    </div>;
}
export default StripeCCFields;